/**
 * Flatten source-level clipPaths in a composed production SVG into plain
 * geometry, so the file survives importers that ignore clipping. CorelDRAW's
 * SVG import drops clip-path'd artwork wholesale, and masterfile atlases mask
 * every panel shape with the panel silhouette (fabric serializes each clipped
 * object as `<g clip-path="url(#id)"><clipPath …/><path …/></g>` with the def
 * INLINE), which made whole front/back panels vanish on import.
 *
 * These clips do real cropping work (trim shapes genuinely overhang their
 * silhouettes), so a lossless rewrite needs true boolean geometry: each shape
 * is replaced by `shape ∩ clip`, computed curve-aware with paper.js (lazy
 * loaded — the admin bundle only pays for it when a file actually contains
 * clips). The result is what production wants anyway: plain filled paths.
 *
 * Anything a boolean can't reproduce (visible strokes, images, text, nested
 * clips, gradients) keeps its clip and is counted in `kept`; those defs are
 * relocated into the root <defs> — spec-neutral, and gives importers that
 * only resolve defs-level clipPaths a chance.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface FlattenClipsResult {
  svg: string;
  /** Clipped elements rewritten to plain intersection geometry. */
  flattened: number;
  /** Clips that had to stay (clip-ignoring importers render these wrong). */
  kept: number;
}

/** 2D affine matrix [a b c d e f] (SVG order). */
export type Mat = [number, number, number, number, number, number];

export const IDENTITY: Mat = [1, 0, 0, 1, 0, 0];

export function multiplyMat(m: Mat, n: Mat): Mat {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

/** Parse an SVG transform list (matrix/translate/scale/rotate) into one matrix. */
export function parseTransform(attr: string | null): Mat {
  if (!attr) return IDENTITY;
  let m: Mat = IDENTITY;
  const re = /(matrix|translate|scale|rotate)\s*\(([^)]*)\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(attr))) {
    const args = match[2]
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    if (args.some((n) => !Number.isFinite(n))) continue;
    switch (match[1]) {
      case 'matrix':
        if (args.length === 6) m = multiplyMat(m, args as Mat);
        break;
      case 'translate':
        m = multiplyMat(m, [1, 0, 0, 1, args[0] ?? 0, args[1] ?? 0]);
        break;
      case 'scale':
        m = multiplyMat(m, [args[0] ?? 1, 0, 0, args[1] ?? args[0] ?? 1, 0, 0]);
        break;
      case 'rotate': {
        const rad = ((args[0] ?? 0) * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        let r: Mat = [cos, sin, -sin, cos, 0, 0];
        if (args.length === 3) {
          r = multiplyMat(multiplyMat([1, 0, 0, 1, args[1], args[2]], r), [
            1,
            0,
            0,
            1,
            -args[1],
            -args[2],
          ]);
        }
        m = multiplyMat(m, r);
        break;
      }
    }
  }
  return m;
}

/** A shape's outline as SVG path data, or null for unsupported geometry. */
export function shapeToPathData(el: Element): string | null {
  const num = (name: string, fallback = 0) => {
    const v = parseFloat(el.getAttribute(name) ?? '');
    return Number.isFinite(v) ? v : fallback;
  };
  switch (el.tagName.toLowerCase()) {
    case 'path':
      return el.getAttribute('d');
    case 'rect': {
      // fabric emits rx="0" ry="0" on square rects — only real rounding bails.
      if (num('rx') > 0 || num('ry') > 0) return null;
      const x = num('x');
      const y = num('y');
      const w = num('width');
      const h = num('height');
      if (w <= 0 || h <= 0) return null;
      return `M${x} ${y}h${w}v${h}h${-w}z`;
    }
    case 'polygon':
    case 'polyline': {
      const pts = (el.getAttribute('points') ?? '')
        .split(/[\s,]+/)
        .filter(Boolean)
        .map(Number);
      if (pts.length < 6 || pts.some((n) => !Number.isFinite(n))) return null;
      let d = `M${pts[0]} ${pts[1]}`;
      for (let i = 2; i < pts.length - 1; i += 2) d += `L${pts[i]} ${pts[i + 1]}`;
      // A polyline is an OPEN outline: closing it would change how a stroke
      // renders. Fills auto-close either way.
      return el.tagName.toLowerCase() === 'polygon' ? d + 'z' : d;
    }
    case 'circle':
    case 'ellipse': {
      const cx = num('cx');
      const cy = num('cy');
      const rx = el.tagName.toLowerCase() === 'circle' ? num('r') : num('rx');
      const ry = el.tagName.toLowerCase() === 'circle' ? num('r') : num('ry');
      if (rx <= 0 || ry <= 0) return null;
      return (
        `M${cx - rx} ${cy}` +
        `A${rx} ${ry} 0 1 0 ${cx + rx} ${cy}` +
        `A${rx} ${ry} 0 1 0 ${cx - rx} ${cy}z`
      );
    }
    default:
      return null;
  }
}

/**
 * A fabric clip object's outline, read from its own `toSVG()` markup.
 *  - `matrix` places the outline's points in the clip owner's space (the
 *    whole transform chain, the shape element's own transform included —
 *    for a Path that is fabric's `translate(-pathOffset)`).
 *  - `placement` is the same chain WITHOUT the shape element's own transform:
 *    where the clip object itself (its centre) sits in the owner's space.
 */
export interface ClipOutline {
  d: string;
  matrix: Mat;
  placement: Mat;
}

export function clipOutlineFromMarkup(markup: string): ClipOutline | null {
  const doc = new DOMParser().parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${markup}</svg>`,
    'image/svg+xml'
  );
  const shape = doc.querySelector('path, polygon, polyline, rect, circle, ellipse');
  const d = shape ? shapeToPathData(shape) : null;
  if (!shape || !d) return null;
  const chain: Element[] = [];
  for (let el: Element | null = shape; el && el !== doc.documentElement; el = el.parentElement) {
    chain.unshift(el);
  }
  let placement: Mat = IDENTITY;
  chain.slice(0, -1).forEach((el) => {
    placement = multiplyMat(placement, parseTransform(el.getAttribute('transform')));
  });
  const matrix = multiplyMat(placement, parseTransform(shape.getAttribute('transform')));
  return { d, matrix, placement };
}

/**
 * Matrix placing a NESTED clip's outline (fabric: `clip.clipPath`) in the
 * outer clip owner's space. fabric positions a clip's clip relative to the
 * outer clip's centre — `outer.placement` — not through the outer outline's
 * own `translate(-pathOffset)`. Composing with `outer.matrix` applied that
 * offset twice: harmless for a rect outer clip (no such offset), but a path
 * outer clip moved the inner one by the path's whole offset — order 28404's
 * style-transfer sleeves came out "clipped away" (left) or cut short (right).
 */
export function nestedClipMatrix(outer: ClipOutline, nested: ClipOutline): Mat {
  return multiplyMat(outer.placement, nested.matrix);
}

export type FillRule = 'nonzero' | 'evenodd';

export interface ClipIntersectEngine {
  /**
   * `content ∩ clip` as path data in the content's PARENT space (both inputs
   * pre-transformed by their matrices). Empty string = nothing visible;
   * null = the engine couldn't compute it (keep the clip).
   */
  intersect(
    content: { d: string; matrix: Mat; fillRule: FillRule },
    clips: Array<{ d: string; matrix: Mat; fillRule: FillRule }>
  ): string | null;
}

/** The slice of paper.js we use (its bundled types don't survive the
 *  instance-as-module export, so type the surface structurally). */
interface PaperItem {
  fillRule: string;
  pathData: string;
  isEmpty(): boolean;
  transform(matrix: unknown): void;
  unite(other: PaperItem, opts: { insert: boolean }): PaperItem;
  intersect(other: PaperItem, opts: { insert: boolean }): PaperItem;
  flatten(tolerance: number): void;
  children?: PaperItem[];
  segments?: Array<{ point: { x: number; y: number } }>;
  closed?: boolean;
}

interface PaperLike {
  project: unknown;
  setup(size: unknown): void;
  Size: new (w: number, h: number) => unknown;
  Matrix: new (a: number, b: number, c: number, d: number, e: number, f: number) => unknown;
  CompoundPath: new (init: { pathData: string; insert: boolean }) => PaperItem;
}

/** One path plus how it sits in the shared target space. */
export interface PathSpec {
  d: string;
  matrix: Mat;
  fillRule: FillRule;
}

let paperPromise: Promise<PaperLike | null> | null = null;

/** paper.js, loaded once on first geometric need (null where unavailable). */
function getPaper(): Promise<PaperLike | null> {
  if (!paperPromise) {
    paperPromise = import('paper')
      .then((mod) => {
        const paper = ((mod as { default?: unknown }).default ?? mod) as unknown as PaperLike;
        if (!paper.project) paper.setup(new paper.Size(1, 1));
        return paper;
      })
      .catch(() => null);
  }
  return paperPromise;
}

function paperItem(paper: PaperLike, spec: PathSpec): PaperItem {
  const item = new paper.CompoundPath({ pathData: spec.d, insert: false });
  item.fillRule = spec.fillRule;
  const [a, b, c, d, e, f] = spec.matrix;
  item.transform(new paper.Matrix(a, b, c, d, e, f));
  return item;
}

/** Curve-aware boolean engine backed by paper.js (lazy singleton). */
async function getPaperEngine(): Promise<ClipIntersectEngine | null> {
  const paper = await getPaper();
  if (!paper) return null;
  return {
    intersect(content, clips) {
      try {
        let clipArea: PaperItem | null = null;
        for (const clip of clips) {
          const item = paperItem(paper, clip);
          clipArea = clipArea ? clipArea.unite(item, { insert: false }) : item;
        }
        if (!clipArea) return null;
        const result = paperItem(paper, content).intersect(clipArea, { insert: false });
        return result.isEmpty() ? '' : (result.pathData ?? null);
      } catch {
        return null;
      }
    },
  };
}

/**
 * True union of several paths, returned as path data transformed by `post`
 * (curve-aware, via paper.js). Used to build a piece's cut SILHOUETTE for the
 * laser outlines: the union of a panel's fabric shapes, optionally clipped to
 * `clip` (the piece window — discards foreign zone shapes that ride along in
 * shared pattern groups), mapped from viewBox space into piece-local points.
 * Null when the union can't be computed.
 */
export async function unionPathData(
  shapes: PathSpec[],
  post: Mat,
  clip?: PathSpec | null
): Promise<string | null> {
  if (!shapes.length) return null;
  const paper = await getPaper();
  if (!paper) return null;
  try {
    let area: PaperItem | null = null;
    for (const shape of shapes) {
      const item = paperItem(paper, shape);
      area = area ? area.unite(item, { insert: false }) : item;
    }
    if (!area || area.isEmpty()) return null;
    if (clip) {
      area = area.intersect(paperItem(paper, clip), { insert: false });
      if (area.isEmpty()) return null;
    }
    const [a, b, c, d, e, f] = post;
    area.transform(new paper.Matrix(a, b, c, d, e, f));
    return area.pathData || null;
  } catch {
    return null;
  }
}

export interface FlatPolyline {
  points: Array<[number, number]>;
  closed: boolean;
}

/**
 * Flatten path data into straight polylines (curves subdivided until they
 * deviate less than `tolerance` from the true curve), transformed by
 * `matrix`. Used to hand cut silhouettes to machines that speak polylines
 * (DXF). Null where paper.js is unavailable.
 */
export async function flattenPathData(
  d: string,
  matrix: Mat,
  tolerance: number
): Promise<FlatPolyline[] | null> {
  const paper = await getPaper();
  if (!paper) return null;
  try {
    const item = paperItem(paper, { d, matrix, fillRule: 'nonzero' });
    item.flatten(tolerance);
    const paths = item.children?.length ? item.children : [item];
    const out: FlatPolyline[] = [];
    for (const p of paths) {
      const segs = p.segments ?? [];
      if (segs.length < 2) continue;
      out.push({
        points: segs.map((s) => [s.point.x, s.point.y] as [number, number]),
        closed: !!p.closed,
      });
    }
    return out;
  } catch {
    return null;
  }
}

function styleProp(el: Element, prop: string): string | null {
  const direct = el.getAttribute(prop);
  if (direct) return direct;
  const style = el.getAttribute('style') ?? '';
  return new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*([^;]+)`).exec(style)?.[1]?.trim() ?? null;
}

function fillRuleOf(el: Element, ruleProp: 'fill-rule' | 'clip-rule'): FillRule {
  return styleProp(el, ruleProp) === 'evenodd' ? 'evenodd' : 'nonzero';
}

function hasVisibleStroke(el: Element): boolean {
  const stroke = styleProp(el, 'stroke');
  return !!stroke && stroke !== 'none' && stroke !== 'transparent';
}

function clipReferenceId(el: Element): string | null {
  const attr = el.getAttribute('clip-path') ?? '';
  return /url\(\s*["']?#([^"')\s]+)["']?\s*\)/.exec(attr)?.[1] ?? null;
}

const SHAPE_TAGS = new Set(['path', 'rect', 'polygon', 'polyline', 'circle', 'ellipse']);
const IGNORABLE_TAGS = new Set(['desc', 'title', 'metadata', 'clippath']);

interface ContentShape {
  el: Element;
  d: string;
  /** Accumulated transforms from the clipped element (exclusive) down to el (inclusive). */
  matrix: Mat;
}

/** Collect the clipped element's paintable shapes with accumulated matrices.
 *  Returns null when the subtree holds anything a boolean can't reproduce. */
function collectContentShapes(root: Element): ContentShape[] | null {
  const shapes: ContentShape[] = [];
  const walk = (el: Element, matrix: Mat): boolean => {
    for (const child of Array.from(el.children)) {
      const tag = child.tagName.toLowerCase();
      if (IGNORABLE_TAGS.has(tag)) continue;
      if (SHAPE_TAGS.has(tag)) {
        if (hasVisibleStroke(child) || child.getAttribute('clip-path')) return false;
        const paint = styleProp(child, 'fill');
        if (paint && paint.startsWith('url(')) return false; // patterns/gradients
        const d = shapeToPathData(child);
        if (d === null) return false;
        shapes.push({
          el: child,
          d,
          matrix: multiplyMat(matrix, parseTransform(child.getAttribute('transform'))),
        });
      } else if (tag === 'g') {
        if (child.getAttribute('clip-path')) return false;
        if (!walk(child, multiplyMat(matrix, parseTransform(child.getAttribute('transform'))))) {
          return false;
        }
      } else {
        return false; // image / text / use / foreignObject …
      }
    }
    return true;
  };
  return walk(root, IDENTITY) ? shapes : null;
}

/** New <path> carrying the source shape's paint and the intersection data.
 *  Boolean output is nonzero-wound, so any inherited evenodd is overridden. */
function flattenedPath(doc: Document, source: Element, d: string): Element {
  const path = doc.createElementNS(SVG_NS, 'path');
  copyPaintAttributes(source, path);
  path.setAttribute('d', d);
  const style = path.getAttribute('style');
  if (style && /fill-rule/.test(style)) {
    path.setAttribute('style', `${style}; fill-rule: nonzero`);
  }
  path.setAttribute('fill-rule', 'nonzero');
  return path;
}

function copyPaintAttributes(from: Element, to: Element): void {
  for (const attr of Array.from(from.attributes)) {
    const name = attr.name;
    if (
      name === 'd' ||
      name === 'transform' ||
      name === 'points' ||
      name === 'clip-path' ||
      /^(x|y|width|height|rx|ry|cx|cy|r)$/.test(name)
    ) {
      continue;
    }
    to.setAttribute(name, attr.value);
  }
}

export async function flattenSourceClipPaths(
  svgText: string,
  engineOverride?: ClipIntersectEngine | null
): Promise<FlattenClipsResult> {
  const unchanged: FlattenClipsResult = { svg: svgText, flattened: 0, kept: 0 };
  if (typeof DOMParser === 'undefined') return unchanged;
  if (!/clip-path/.test(svgText)) return unchanged;

  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length) return unchanged;
  const svgEl = doc.documentElement;
  const clippedEls = Array.from(svgEl.querySelectorAll('[clip-path]'));
  if (!clippedEls.length) return unchanged;

  const engine = engineOverride !== undefined ? engineOverride : await getPaperEngine();
  if (!engine) return { ...unchanged, kept: clippedEls.length };

  const clipDefs = new Map<string, Element>();
  for (const def of Array.from(svgEl.querySelectorAll('clipPath'))) {
    const id = def.getAttribute('id');
    if (id) clipDefs.set(id, def);
  }

  let flattened = 0;
  let kept = 0;

  for (const el of clippedEls) {
    const refId = clipReferenceId(el);
    const def = refId ? clipDefs.get(refId) : null;
    const units = def?.getAttribute('clipPathUnits');
    if (!def || (units && units !== 'userSpaceOnUse')) {
      kept++;
      continue;
    }

    // The clip applies in the element's local space — same space as its
    // children (fabric authors both there).
    const clips: Array<{ d: string; matrix: Mat; fillRule: FillRule }> = [];
    let clipsOk = true;
    for (const kid of Array.from(def.children)) {
      if (IGNORABLE_TAGS.has(kid.tagName.toLowerCase())) continue;
      const d = shapeToPathData(kid);
      if (d === null) {
        clipsOk = false;
        break;
      }
      clips.push({
        d,
        matrix: parseTransform(kid.getAttribute('transform')),
        fillRule: fillRuleOf(kid, 'clip-rule'),
      });
    }
    if (!clipsOk || !clips.length) {
      kept++;
      continue;
    }

    const isSelfShape = SHAPE_TAGS.has(el.tagName.toLowerCase());
    const shapes = isSelfShape
      ? !hasVisibleStroke(el) && shapeToPathData(el) !== null
        ? [{ el, d: shapeToPathData(el)!, matrix: IDENTITY }]
        : null
      : collectContentShapes(el);
    if (shapes === null) {
      kept++;
      continue;
    }

    // Compute every intersection before touching the DOM, so a failed shape
    // leaves the whole element (and its clip) intact.
    const results: Array<{ shape: ContentShape; d: string }> = [];
    let allOk = true;
    for (const shape of shapes) {
      const d = engine.intersect(
        { d: shape.d, matrix: shape.matrix, fillRule: fillRuleOf(shape.el, 'fill-rule') },
        clips
      );
      if (d === null) {
        allOk = false;
        break;
      }
      results.push({ shape, d });
    }
    if (!allOk) {
      kept++;
      continue;
    }

    if (isSelfShape) {
      const d = results[0]?.d ?? '';
      if (!d) {
        el.remove(); // clipped fully away — was never visible
      } else {
        const flat = flattenedPath(doc, el, d);
        // The clip applied in the shape's local space, so the baked geometry
        // still belongs under the shape's own placement transform.
        const transform = el.getAttribute('transform');
        if (transform) flat.setAttribute('transform', transform);
        el.replaceWith(flat);
      }
    } else {
      for (const { shape, d } of results) {
        shape.el.remove();
        // Ancestor transforms are baked into the intersection data, so the
        // path hoists to the clipped element itself. Walk order = document
        // order, so sequential appends preserve paint order.
        if (d) el.appendChild(flattenedPath(doc, shape.el, d));
      }
      // Drop group wrappers that only existed to carry transforms/shapes.
      for (const child of Array.from(el.children)) {
        if (child.tagName.toLowerCase() === 'g' && !child.querySelector('*:not(g)')) {
          child.remove();
        }
      }
      el.removeAttribute('clip-path');
    }
    flattened++;
  }

  // Prune defs nothing references; relocate survivors into root <defs> so
  // importers that only resolve defs-level clipPaths can still find them.
  const stillReferenced = new Set(
    Array.from(svgEl.querySelectorAll('[clip-path]'))
      .map(clipReferenceId)
      .filter((id): id is string => id !== null)
  );
  let rootDefs: Element | null = null;
  for (const [id, def] of clipDefs) {
    if (!stillReferenced.has(id)) {
      def.remove();
    } else if (def.parentElement && def.parentElement.tagName.toLowerCase() !== 'defs') {
      if (!rootDefs) {
        rootDefs =
          Array.from(svgEl.children).find((c) => c.tagName.toLowerCase() === 'defs') ?? null;
        if (!rootDefs) {
          rootDefs = doc.createElementNS(SVG_NS, 'defs');
          svgEl.insertBefore(rootDefs, svgEl.firstChild);
        }
      }
      rootDefs.appendChild(def);
    }
  }

  return { svg: new XMLSerializer().serializeToString(svgEl), flattened, kept };
}

export interface ConvertShapesResult {
  svg: string;
  converted: number;
}

const GEOMETRY_ATTRS = /^(x|y|x1|y1|x2|y2|width|height|rx|ry|cx|cy|r|points)$/;

/**
 * Rewrite basic shape elements (rect/polygon/polyline/circle/ellipse/line)
 * as equivalent <path> elements. CorelDRAW imports rects as native rectangle
 * objects and botches ones under negative-scale transform chains (a fabric
 * composite flips twice: the root scale(1,-1) and the design matrix) — the
 * lacrosse collar rect came in as a giant diagonal smear. Paths are the one
 * primitive its importer handles reliably. Everything else (paint, transform,
 * id) is untouched, so rendering is byte-for-byte equivalent.
 *
 * Rounded rects keep their element (path conversion would need arc stitching
 * nobody has needed yet).
 */
export function convertBasicShapesToPaths(svgText: string): ConvertShapesResult {
  const unchanged: ConvertShapesResult = { svg: svgText, converted: 0 };
  if (typeof DOMParser === 'undefined') return unchanged;
  if (!/<(rect|polygon|polyline|circle|ellipse|line)[\s/>]/.test(svgText)) return unchanged;

  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length) return unchanged;

  let converted = 0;
  const shapes = Array.from(
    doc.documentElement.querySelectorAll('rect, polygon, polyline, circle, ellipse, line')
  );
  for (const el of shapes) {
    let d: string | null;
    if (el.tagName.toLowerCase() === 'line') {
      const num = (name: string) => {
        const v = parseFloat(el.getAttribute(name) ?? '');
        return Number.isFinite(v) ? v : 0;
      };
      d = `M${num('x1')} ${num('y1')}L${num('x2')} ${num('y2')}`;
    } else {
      d = shapeToPathData(el);
    }
    if (!d) continue;
    const path = doc.createElementNS(SVG_NS, 'path');
    for (const attr of Array.from(el.attributes)) {
      if (GEOMETRY_ATTRS.test(attr.name)) continue;
      path.setAttribute(attr.name, attr.value);
    }
    path.setAttribute('d', d);
    el.replaceWith(path);
    converted++;
  }
  if (!converted) return unchanged;
  return { svg: new XMLSerializer().serializeToString(doc.documentElement), converted };
}
